/**
 * Kleiner Toast-Helfer, der auch außerhalb von Komponenten funktioniert
 * (z. B. in Router-Guards). `FlashToaster.vue` meldet die PrimeVue-Senke an;
 * bis dahin werden Meldungen zwischengespeichert.
 */

export type ToastSeverity = 'success' | 'info' | 'warn' | 'error'

export interface ToastMessage {
  severity: ToastSeverity
  summary?: string
  detail: string
  /** Anzeigedauer in ms. */
  life?: number
}

type ToastSink = (message: ToastMessage) => void

let sink: ToastSink | null = null
const pending: ToastMessage[] = []

const DEFAULT_LIFE: Record<ToastSeverity, number> = {
  success: 3000,
  info: 4000,
  warn: 6000,
  error: 8000,
}

export function registerToastSink(next: ToastSink): void {
  sink = next
  while (pending.length) {
    const message = pending.shift()
    if (message) sink(message)
  }
}

export function unregisterToastSink(candidate?: ToastSink): void {
  if (!candidate || sink === candidate) sink = null
}

export function notify(message: ToastMessage): void {
  const enriched: ToastMessage = { life: DEFAULT_LIFE[message.severity], ...message }
  if (sink) sink(enriched)
  else pending.push(enriched)
}

export const toast = {
  success: (detail: string, summary?: string) => notify({ severity: 'success', detail, summary }),
  info: (detail: string, summary?: string) => notify({ severity: 'info', detail, summary }),
  warn: (detail: string, summary?: string) => notify({ severity: 'warn', detail, summary }),
  error: (detail: string, summary?: string) => notify({ severity: 'error', detail, summary }),
}

/** Composable-Fassade (identisch zu `toast`, nur für die Lesbarkeit). */
export function useToast() {
  return toast
}

/** Nur für Tests. */
export function __resetToasts(): void {
  sink = null
  pending.length = 0
}
